import isEqual from 'hs-lodash/isEqual';
import Action from 'automation-ui-action-features/records/Action';
import { EdgeType } from 'automation-ui-directed-graph/Edge';
import * as EnrollmentFilterOperators from 'customer-data-filters/converters/objectSeg/ObjectSegOperatorTypes';
import { translateToGraph } from 'automation-ui-canvas/utils/platformWorkflowTranslators';
import { createReverseEdgeMap, getPreviousActionId, getEdgeModifier } from 'automation-ui-action-features/records/recordsUtils';
import StaticBranchConnection from 'automation-ui-action-features/records/connection/StaticBranchConnection';
import { LIST_BRANCH_LIMIT, STATIC_BRANCH_LIMIT } from 'automation-ui-action-features/actions/list-branch/constants/ListBranchConstants';
import { STATIC_BRANCH } from 'automation-ui-action-features/actions/ActionMetadataConfigs';
import { OBJECT_PROPERTY } from 'automation-ui-action-features/data-passing/constants/DataSourceOptions';
import { List } from 'immutable';
import ConnectionTypes from 'automation-ui-action-features/constants/ConnectionTypes';
import memoize from 'transmute/memoize';

/**
 * Consolidation features in order of operations:
 * - LIST_BRANCH actions that contain only "single equality check" filters
 *   turn into a STATIC_BRANCH action
 * - Cascading LIST_BRANCH or STATIC_BRANCH actions turn into single actions
 *   with multiple `branches` (up to their respective max branches allowed)
 *   when they have defaultConnections pointing to other branches of the
 *   same type. Those child branch actions must not have GO_TO parent edges
 * - LIST_BRANCH actions that contain multiple equality checks per branch
 *   may one day turn into STATIC_BRANCH actions with GO_TO actions built in
 */

const doesNodeHaveGoToPathParents = (targetNodeId, goToPaths) => Object.values(goToPaths).some(({
  toNodeId
}) => targetNodeId === toNodeId);

/******************************************************
 ********* LIST BRANCH TO STATIC BRANCH ***************
 ******************************************************/

/**
 * For now, there must be a single equality filter to pass this check.
 * However, in the future when STATIC_BRANCH actions can support
 * multiple values for the same branch, we should enhance this
 * method to allow multiple ORs. Or we could implement GO_TO actions
 * that point to other branches of the STATIC_BRANCH action.
 *
 * Conditions to be met:
 *
 * - The 1 filter contains exactly 1 equality operator
 * - The filter's `filterType` must be PROPERTY (for now)
 * - The filter does not contain a multi value equality check

 */
export const filterIsSingleEqualityCheck = filterBranches => {
  // this check could change if we start supporting multiple values
  // in the STATIC_BRANCH action
  if (filterBranches.length !== 1 || !filterBranches[0].filters || filterBranches[0].filters.length !== 1) {
    return false;
  }
  const filter = filterBranches[0].filters[0];
  if (filter.filterType !== 'PROPERTY' || filter.operation.operator !== EnrollmentFilterOperators.IS_EQUAL_TO || filter.operation.value === undefined) {
    return false;
  }
  return true;
};
export const canListBranchBeTransformed = action => {
  const connection = action.getConnection();
  const listBranches = connection.getBranches();
  if (listBranches.size !== 1) {
    return false;
  }
  const listBranch = listBranches.get(0);
  if (!listBranch.filterBranch) {
    return false;
  }
  const filterBranches = listBranch.filterBranch.filterBranches;
  return filterIsSingleEqualityCheck(filterBranches);
};

/**
 * Returns a Static branch action to be added to a flow
 */
export const listBranchToStaticBranch = action => {
  const connection = action.getConnection();
  const listBranches = connection.getBranches();
  if (listBranches.size !== 1) {
    return null;
  }
  const defaultBranchName = connection.getDefaultBranchName();
  const listBranch = listBranches.get(0);
  const filterBranches = listBranch.filterBranch.filterBranches;
  const canTransform = canListBranchBeTransformed(action);
  if (!canTransform) {
    return null;
  }

  // Assumes filterType === 'PROPERTY', which is the `value` on the enrolled object
  const propertyFilter = filterBranches[0].filters[0];
  const equalityPropertyName = propertyFilter.property;
  const equalityPropertyOperation = propertyFilter.operation;
  const equalityPropertyValue = equalityPropertyOperation.value;
  const staticBranchConnection = new StaticBranchConnection({
    connectionType: ConnectionTypes.STATIC_BRANCH_CONNECTION,
    defaultBranchName,
    defaultConnection: connection.getDefaultConnector(),
    connections: [{
      value: equalityPropertyValue,
      propertyValue: null,
      connection: listBranch.getConnector()
    }]
  });
  const newAction = new Action({
    portalId: action.portalId,
    flowId: action.flowId,
    actionId: action.actionId,
    // the BE has sanctioned this very special case
    // which allows us to "change" the action type of an action
    actionType: STATIC_BRANCH.type,
    actionTypeId: STATIC_BRANCH.typeId,
    connection: staticBranchConnection,
    metadata: {
      inputValue: {
        propertyName: equalityPropertyName,
        type: OBJECT_PROPERTY
      }
    }
  });
  return newAction;
};
export const getAllListBranchesToTransformToStaticBranches = memoize(flow => {
  const graph = translateToGraph(flow, false);
  const rootListBranchActionIds = [];
  graph.nodeIds.forEach(nodeId => {
    const action = flow.getActionById(nodeId);
    if (action && action.isListBranch()) {
      const canTransform = canListBranchBeTransformed(action);
      if (canTransform) {
        rootListBranchActionIds.push(nodeId);
      }
    }
  });
  return rootListBranchActionIds;
}, flow => flow.version);
export const transformAllListBranchActionsToStaticBranchActions = (flow, transformableListBranchActionIds) => {
  let newFlow = flow;
  transformableListBranchActionIds.forEach(nodeId => {
    const action = flow.getActionById(nodeId);
    const canTransform = canListBranchBeTransformed(action);
    if (canTransform) {
      const newAction = listBranchToStaticBranch(action) || action;
      const actions = newFlow.getActions();
      const updatedActions = actions.set(newAction.getActionId(), newAction);
      newFlow = newFlow.setActions(updatedActions);
    }
  });
  return newFlow;
};

/******************************************************
 ***************** CONSOLIDATION **********************
 ******************************************************/

/**
 * To consolidate, the following must be true for the branch action:
 * - The action must be STATIC_BRANCH or LIST_BRANCH
 * - There is another branch action in the default
 *   connection
 * - There must be no GoTo actions pointing to the
 *   branch action because it can cause cycles
 * - The action must not have reached its limit of
 *   branches allowed
 */
export const cascadingBranchActionCanBeConsolidated = (flow, action, goToPaths) => {
  const connection = action.getConnection();
  const defaultConnection = connection.getDefaultConnector();
  if (!defaultConnection || defaultConnection.edgeType !== EdgeType.STANDARD) {
    return false;
  }
  const nextActionId = defaultConnection.getNextActionId();
  if (!nextActionId) {
    return false;
  }
  const nextAction = flow.getActionById(nextActionId);
  if (!nextAction || action.isListBranch() && !nextAction.isListBranch() || action.isStaticBranch() && !nextAction.isStaticBranch()) {
    return false;
  }
  if (doesNodeHaveGoToPathParents(nextActionId, goToPaths || {})) {
    return false;
  }
  const numOfBranches = connection.getBranches().size;
  const nextActionConnection = nextAction.getConnection();
  const nextActionNumOfBranches = nextActionConnection ? nextActionConnection.getBranches().size : 0;
  if (action.isListBranch() && numOfBranches + nextActionNumOfBranches > LIST_BRANCH_LIMIT || action.isStaticBranch() && numOfBranches + nextActionNumOfBranches > STATIC_BRANCH_LIMIT) {
    return false;
  }
  return true;
};
export const combineBranchActions = (parentAction, childAction) => {
  if (parentAction.actionType !== childAction.actionType) {
    return parentAction;
  }
  const parentConnection = parentAction.getConnection();
  const childConnection = childAction.getConnection();
  const parentBranches = parentConnection.getBranches();
  const childBranches = childConnection.getBranches();
  const defaultChildConnection = childConnection.getDefaultConnector();
  const newParentBranches = List(parentBranches.concat(childBranches));
  const newParentConnection = parentConnection.setBranches(newParentBranches).setDefaultBranchName(childConnection.getDefaultBranchName()).setDefaultConnector(defaultChildConnection);
  const newParentAction = parentAction.setConnection(newParentConnection);
  return newParentAction;
};
const FILTER_BRANCH_UNIQUE_FIELDS = new Set(['associationListId']);
export function sanitizeFilterBranchForDeduplication(filterBranch) {
  return JSON.parse(JSON.stringify(filterBranch, (key, value) => {
    if (FILTER_BRANCH_UNIQUE_FIELDS.has(key)) {
      return undefined;
    } else {
      return value;
    }
  }));
}

/**
 * Starting at a root branch action, return a new flow with
 * one (or multiple) branch action with up to the max number
 * of branches allowed.
 */
export const consolidateBranchAction = (action, flow) => {
  let newFlow = flow;
  let newAction = action;
  const graph = translateToGraph(flow, false);
  const goToPaths = graph.getGoToPaths();
  const actionId = action.getActionId();
  const defaultEdgeId = `${actionId}-edge-1`;
  const connection = action.getConnection();
  const numOfBranches = connection ? connection.getBranches().size : 0;
  let numOfBranchesLeft = (action.isListBranch() ? LIST_BRANCH_LIMIT : STATIC_BRANCH_LIMIT) - numOfBranches;
  while (numOfBranchesLeft > 0) {
    const currentAction = newFlow.getActionById(actionId);
    const canConsolidateBranches = cascadingBranchActionCanBeConsolidated(flow, currentAction, goToPaths);
    if (canConsolidateBranches) {
      // we know this action is a branch and it has a default connection
      const nextAction = newFlow.getActionById(currentAction.getNextActionIdByEdgeId(defaultEdgeId));
      const nextActionConnection = nextAction.getConnection();
      const nextActionNumOfBranches = nextActionConnection ? nextActionConnection.getBranches().size : 0;
      newAction = combineBranchActions(currentAction, nextAction);

      // remove duplicate branches and their actions since they can't be reached
      if (newAction.isListBranch()) {
        const newActionConnection = newAction.getConnection();
        const branches = newActionConnection.getBranches();
        let deduplicatedBranches = List();
        const uniqueFilterBranches = [];

        // eslint-disable-next-line no-loop-func
        branches.forEach(branch => {
          const sanitizedFilterBranch = sanitizeFilterBranchForDeduplication(branch.filterBranch);
          if (uniqueFilterBranches.some(deduplicatedFilterBranch => isEqual(sanitizedFilterBranch, deduplicatedFilterBranch))) {
            const connector = branch.getConnector();
            if (connector && connector.nextActionId) {
              newFlow = newFlow.removeAction(connector.nextActionId, true);
            }
          } else {
            deduplicatedBranches = deduplicatedBranches.push(branch);
            uniqueFilterBranches.push(sanitizedFilterBranch);
          }
        });
        newAction = newAction.setConnection(newActionConnection.setBranches(deduplicatedBranches));
      } else if (newAction.isStaticBranch()) {
        const newActionConnection = newAction.getConnection();
        const branches = newActionConnection.getBranches();
        let deduplicatedBranches = List();
        const uniqueValues = new Set();

        // eslint-disable-next-line no-loop-func
        branches.forEach(branch => {
          const value = branch.getBranchName();
          if (uniqueValues.has(value)) {
            const connector = branch.getConnector();
            if (connector && connector.nextActionId) {
              newFlow = newFlow.removeAction(connector.nextActionId, true);
            }
          } else {
            deduplicatedBranches = deduplicatedBranches.push(branch);
            uniqueValues.add(value);
          }
          newAction = newAction.setConnection(newActionConnection.setBranches(deduplicatedBranches));
        });
      }
      const nextActionId = nextAction.getActionId();
      newFlow = newFlow.setActions(newFlow.getActions().set(actionId, newAction).remove(nextActionId)).setCollapsedBranchPointers(nextActionId, actionId);
      numOfBranchesLeft = numOfBranchesLeft - nextActionNumOfBranches;
    } else {
      numOfBranchesLeft = 0;
    }
  }
  return newFlow;
};
export const getAllBranchesToBeConsolidated = memoize(flow => {
  const actions = flow.getActions();
  const graph = translateToGraph(flow, false);
  const reverseEdgeMap = createReverseEdgeMap(actions);
  const goToPaths = graph.getGoToPaths();
  const rootNodeIdsOfListBranchActionsWithChildren = [];
  graph.nodeIds.forEach(nodeId => {
    const action = flow.getActionById(nodeId);
    if (action && (action.isListBranch() || action.isStaticBranch())) {
      const parentEdges = reverseEdgeMap[nodeId] || [];
      const hasListBranchParent = parentEdges.some(edge => {
        const previousEdgeIdModifier = getEdgeModifier(edge.edgeId);
        const previousActionId = getPreviousActionId(edge.edgeId);
        const previousAction = flow.getActionById(previousActionId);
        return previousEdgeIdModifier === 1 &&
        // if not 1 (the default branch) assume it's independent
        previousAction && (action.isListBranch() && previousAction.isListBranch() || action.isStaticBranch() && previousAction.isStaticBranch());
      });
      if (!hasListBranchParent) {
        rootNodeIdsOfListBranchActionsWithChildren.push(nodeId);
      }
    }
  });
  return rootNodeIdsOfListBranchActionsWithChildren.filter(nodeId => cascadingBranchActionCanBeConsolidated(flow, flow.getActionById(nodeId), goToPaths));
}, flow => flow.version);
export const consolidateRootListBranchActions = (flow, rootBranchActionIds) => {
  const graph = translateToGraph(flow, false);
  const goToPaths = graph.getGoToPaths();
  let newFlow = flow;
  rootBranchActionIds.forEach(nodeId => {
    const action = flow.getActionById(nodeId);
    const canConsolidateBranches = cascadingBranchActionCanBeConsolidated(flow, action, goToPaths);
    if (canConsolidateBranches) {
      newFlow = consolidateBranchAction(action, newFlow);
    }
  });
  return newFlow;
};
export const countOfDeletedActionsAfterConsolidation = ({
  flow,
  branchesToConsolidate = []
}) => {
  const nodesLeftToExplore = [...branchesToConsolidate];
  let finalCount = 0;
  let currentConsecutiveConsolidation = 1;
  while (nodesLeftToExplore.length) {
    const currentNode = nodesLeftToExplore.pop();
    const action = flow.getActionById(currentNode);
    const actionId = action.getActionId();
    const actionType = action.getActionType();
    const defaultEdgeId = `${actionId}-edge-1`;
    const maxBranchesAllowed = action.isListBranch() ? LIST_BRANCH_LIMIT : STATIC_BRANCH_LIMIT;
    const nextAction = flow.getActionById(action.getNextActionIdByEdgeId(defaultEdgeId));
    if (nextAction && nextAction.getActionType() === actionType && currentConsecutiveConsolidation < maxBranchesAllowed) {
      finalCount++;
      currentConsecutiveConsolidation++;
      nodesLeftToExplore.push(nextAction.getActionId());
    } else {
      currentConsecutiveConsolidation = 1;
    }
  }
  return finalCount;
};